import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress } from '@material-ui/core';
import { InventoryReportSnapshotItemRow } from '../types';

const KEYBOARD_NAV_CONFIG = {
    useTabKeys: true,
    nextKeys: ['Tab', 'ArrowDown', 'ArrowRight'],
    prevKeys: ['Shift+Tab', 'ArrowUp', 'ArrowLeft'],
};

export interface OrderQtyCellProps {
    value: number;
    editable: boolean;
    unsavedChange?: { originalValue: number; newValue: number } | null;
    rowId: string;
    row: InventoryReportSnapshotItemRow;
    onUpdate: (newValue: number, row: InventoryReportSnapshotItemRow) => Promise<any>;
    onCommitAndNavigate: (newValue: number, oldData: InventoryReportSnapshotItemRow, direction: 'next' | 'prev') => Promise<void>;
    focusRowId?: string | null;
    savingRowId?: string | null;
}

const OrderQtyCell: React.FC<OrderQtyCellProps> = ({
    value,
    editable,
    unsavedChange,
    rowId,
    row,
    onUpdate,
    onCommitAndNavigate,
    focusRowId,
    savingRowId,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (focusRowId === rowId && editable) {
            setIsEditing(true);
            requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            });
        }
    }, [focusRowId, rowId, editable]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const key = e.key;
        const useTab = KEYBOARD_NAV_CONFIG.useTabKeys;
        const isNextKey = useTab ? key === 'Tab' && !e.shiftKey : key === 'ArrowDown' || key === 'ArrowRight';
        const isPrevKey = useTab ? key === 'Tab' && e.shiftKey : key === 'ArrowUp' || key === 'ArrowLeft';

        if (isNextKey || isPrevKey) {
            e.preventDefault();
            const input = e.target as HTMLInputElement;
            const numValue = parseFloat(input.value) || 0;
            const newValue = Math.max(0, numValue);
            const direction = isNextKey ? 'next' : 'prev';
            setIsEditing(false);
            onCommitAndNavigate(newValue, row, direction);
        } else if (key === 'Escape') {
            setIsEditing(false);
        }
    };

    const handleBlur = () => {
        const input = inputRef.current;
        if (input) {
            const numValue = parseFloat(input.value) || 0;
            const newValue = Math.max(0, numValue);
            if (newValue !== value) {
                onUpdate(newValue, row);
            }
        }
        setIsEditing(false);
    };

    const handleDoubleClick = () => {
        if (editable) {
            setIsEditing(true);
            requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            });
        }
    };

    const isSaving = savingRowId === rowId;

    if (isSaving) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', minHeight: '24px' }}>
                <CircularProgress size={18} />
            </div>
        );
    }

    if (isEditing && editable) {
        return (
            <input
                ref={inputRef}
                type="number"
                defaultValue={value}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onChange={(e) => {
                    const v = parseFloat(e.target.value) || 0;
                    if (v >= 0) (e.target as HTMLInputElement).value = String(Math.max(0, v));
                }}
                style={{ width: '100%', padding: '4px', minWidth: 60 }}
                min={0}
            />
        );
    }

    return (
        <div
            onDoubleClick={handleDoubleClick}
            style={{
                cursor: editable ? 'text' : 'default',
                width: '100%',
                padding: '4px',
                backgroundColor: unsavedChange ? '#e8f5e9' : editable ? '#f5f5f5' : 'transparent',
                minHeight: '24px',
            }}
        >
            {unsavedChange ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ textDecoration: 'line-through', color: '#666', fontSize: '0.9em' }}>
                        {unsavedChange.originalValue}
                    </span>
                    <span style={{ color: '#2e7d32', fontWeight: 600 }}>{unsavedChange.newValue}</span>
                </span>
            ) : (
                value
            )}
        </div>
    );
};

function arePropsEqual(prev: OrderQtyCellProps, next: OrderQtyCellProps): boolean {
    if (prev.rowId !== next.rowId) return false;
    if (prev.value !== next.value) return false;
    if (prev.editable !== next.editable) return false;
    if (prev.focusRowId !== next.focusRowId) return false;
    if (prev.savingRowId !== next.savingRowId) return false;
    const prevNew = prev.unsavedChange?.newValue;
    const nextNew = next.unsavedChange?.newValue;
    if (prevNew !== nextNew) return false;
    return true;
}

export default React.memo(OrderQtyCell, arePropsEqual);
